import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { formatCurrencySigFig } from '../../utils/formatters';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import Modal from '../common/Modal/Modal';
import LobsterExpensePieChart from '../LobsterCharts/LobsterExpensePieChart';
import InsightColumnPair from '../InsightColumnPair';
import '../../styles/insightCards.css';

const LOBSTER_DESCRIPTION =
  'Fishery-wide economic metrics and endogenous bait market prices from the integrated lobster-bait equilibrium model. Bait prices are determined through a four-regime CES demand structure with herring and menhaden substitution.';

interface LobsterInsights {
  totalRevenue?: number;
  avgProfit?: number;
  avgBaitCost?: number;
}

interface LobsterFisheryEconomicsData {
  insights?: LobsterInsights | null;
  originalInsights?: LobsterInsights | null;
  lobsterLoading?: boolean;
  showStatusQuoColumn?: boolean;
  showSingleColumn?: boolean;
  showChangeIndicators?: boolean;
  onOpenLobsterExpenseModal?: () => void;
}

function calculateChange(original: number | undefined, current: number | undefined): number | null {
  if (original == null || current == null || original === 0) return null;
  return ((current - original) / original) * 100;
}

function getChangeTintClass(original: number | undefined, current: number | undefined): string {
  const percentChange = calculateChange(original, current);
  if (percentChange === null) return 'tint-grey';
  if (percentChange > 0.01) return 'tint-green';
  if (percentChange < -0.01) return 'tint-red';
  return 'tint-grey';
}

export default function LobsterFisheryEconomics(props: RegistryComponentProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<LobsterFisheryEconomicsData>(props);
  const pageLobsterResults = pageData?.data?.lobsterResults as { annual_results?: unknown[] } | null | undefined;
  const pageInsights = pageData?.data?.insights as LobsterInsights | null | undefined;
  const pageOriginalInsights = pageData?.data?.originalInsights as LobsterInsights | null | undefined;
  const selectedModel = pageData?.data?.selectedModel as string | undefined;
  const insights = data.insights ?? pageInsights;
  const originalInsights = data.originalInsights ?? pageOriginalInsights;
  const lobsterLoading = data.lobsterLoading ?? (pageData?.data?.lobsterLoading as boolean | undefined) ?? false;
  const showStatusQuoColumn = data.showStatusQuoColumn ?? true;
  const showSingleColumn = data.showSingleColumn ?? false;
  const showChangeIndicators = data.showChangeIndicators ?? false;
  const canOpenModal = selectedModel === 'LOBSTER';
  const onOpenLobsterExpenseModal = data.onOpenLobsterExpenseModal ?? (canOpenModal ? () => setShowModal(true) : undefined);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  }, []);

  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener('openLobsterExpenseModal', handler);
    return () => window.removeEventListener('openLobsterExpenseModal', handler);
  }, []);

  const toggleInsight = useCallback(() => setExpanded((e) => !e), []);

  const annualResults = pageLobsterResults?.annual_results ?? [];
  const statusQuoDisplay = (originalInsights ?? insights) as LobsterInsights;

  return (
    <div className="insight-card">
      <div className="insight-title-row">
        <h3 className="insight-title">Lobster Fishery Economics</h3>
        <button
          className="info-icon"
          onClick={toggleInsight}
          aria-label="Toggle description"
          type="button"
        >
          i
        </button>
      </div>
      <div className={`insight-subtitle-container ${expanded ? 'expanded' : ''}`}>
        <h2 className="insight-subtitle">{LOBSTER_DESCRIPTION}</h2>
      </div>

      {lobsterLoading ? (
        <div className="loading-message" style={{ padding: '20px', textAlign: 'center' }}>Loading lobster data...</div>
      ) : !insights ? (
        <div className="loading-message" style={{ padding: '20px', textAlign: 'center' }}>No data available</div>
      ) : (
        <>
          <InsightColumnPair
            originalValue={originalInsights?.totalRevenue}
            currentValue={insights.totalRevenue}
            statusQuoContent={formatCurrencySigFig((statusQuoDisplay.totalRevenue ?? 0) * 1_000_000)}
            currentContent={formatCurrencySigFig((insights.totalRevenue ?? 0) * 1_000_000)}
            showStatusQuoColumn={showStatusQuoColumn}
            showSingleColumn={showSingleColumn}
            showChangeIndicators={Boolean(showChangeIndicators)}
            parametersChanged={false}
            statusQuoHeader=" Fishery Revenue (Status Quo)"
            currentHeader={showStatusQuoColumn ? ' Fishery Revenue (Adjusted)' : ''}
          />

          <InsightColumnPair
            originalValue={originalInsights?.avgProfit}
            currentValue={insights.avgProfit}
            statusQuoContent={
              <>${((statusQuoDisplay.avgProfit ?? 0) / 1000).toFixed(1)}K</>
            }
            currentContent={<>${((insights.avgProfit ?? 0) / 1000).toFixed(1)}K</>}
            showStatusQuoColumn={showStatusQuoColumn}
            showSingleColumn={showSingleColumn}
            showChangeIndicators={Boolean(showChangeIndicators)}
            parametersChanged={false}
            statusQuoHeader="Avg Annual Profit (Status Quo)"
            currentHeader={showStatusQuoColumn ? 'Avg Annual Profit (Adjusted)' : ''}
            wrapperStyle={{ marginTop: '10px' }}
          />

          <InsightColumnPair
            originalValue={originalInsights?.avgBaitCost}
            currentValue={insights.avgBaitCost}
            statusQuoContent={
              <>${((statusQuoDisplay.avgBaitCost ?? 0) / 1000).toFixed(1)}K</>
            }
            currentContent={<>${((insights.avgBaitCost ?? 0) / 1000).toFixed(1)}K</>}
            showStatusQuoColumn={showStatusQuoColumn}
            showSingleColumn={showSingleColumn}
            showChangeIndicators={Boolean(showChangeIndicators)}
            parametersChanged={false}
            onClick={onOpenLobsterExpenseModal}
            clickable={Boolean(onOpenLobsterExpenseModal)}
            statusQuoHeader="Avg Annual Bait Cost (Status Quo)"
            currentHeader={showStatusQuoColumn ? 'Avg Annual Bait Cost (Adjusted)' : ''}
            wrapperStyle={{ marginTop: '10px' }}
          />
        </>
      )}

      {showModal &&
        createPortal(
          <Modal
            title="Lobster Vessel Operating Expenses Breakdown"
            onClose={handleCloseModal}
            size="large"
            transparentBackdrop={true}
            parameterStyle={true}
            {...({ customMaxWidth: '800px', customMaxHeight: '90vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '20px' }}
          >
            <LobsterExpensePieChart annualResults={annualResults} />
          </Modal>,
          document.body
        )}
    </div>
  );
}
